import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FormHelperText,
  Modal,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ActionButton from '../../../../components/ActionButton';
import SpinnerComponent from '../../../../components/SpinnerComponent';
import WarningBanner from '../../../../components/WarningBanner';
import { descriviPubblicazioneCheHaChiuso } from './rimappatureUtils';

/**
 * The width of `ragione` in the DDL, repeated server-side in
 * `ArchiviaVoceRimappaturaRequest` and repeated here: stopped in the form, the
 * refusal does not take a network round trip to say something the form knows.
 */
export const LUNGHEZZA_MASSIMA_RAGIONE = 1024;

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  contenitore: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backgroundColor: 'white',
    color: 'black',
    padding: theme.spacing(4),
    boxShadow: theme.shadows[6],
    borderRadius: '6px',
    overflowY: 'auto',
    width: '90%',
    maxWidth: '620px',
    maxHeight: '90vh',
  },
  campo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  sonda: {
    fontFamily: 'monospace',
  },
  errore: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  pulsanti: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // `marginRight: 'auto'` keeps the status to the left of the buttons: appearing
  // does not shift them, so a click never lands on a target that has moved.
  statoSalvataggio: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginRight: 'auto',
    color: theme.palette.text.secondary,
  },
  spinnerInline: { display: 'flex' },
}));

/**
 * Archiving a voce of the review queue (US 33).
 *
 * The reason is mandatory and it is not paperwork: archiving means deciding that
 * that fridge stays knowingly unwatched, and a decision without the written why
 * would be the silence of US 33 with a placeholder on top.
 */
const ArchiviaVoceModal = ({
  erroreServer,
  onArchivia,
  onClose,
  open,
  salvataggioInCorso,
  voce,
}) => {
  const classes = useStyles();
  const [ragione, setRagione] = useState('');

  const ripulita = ragione.trim();
  const troppoLunga = ripulita.length > LUNGHEZZA_MASSIMA_RAGIONE;
  const archiviabile = Boolean(ripulita) && !troppoLunga && !salvataggioInCorso;

  return (
    /* While a write is in flight the modal does not close: `Annulla`, backdrop and
       ESC would throw away both `erroreServer` and the reason just typed, and the
       server's refusal would reach a modal that no longer exists - leaving the
       technician without knowing whether the voce was archived. */
    <Modal
      open={open}
      onClose={onClose}
      disableBackdropClick={salvataggioInCorso}
      disableEscapeKeyDown={salvataggioInCorso}
      className={classes.modal}
    >
      <div className={classes.contenitore}>
        <Typography variant="h5">Archivia la voce</Typography>

        {/* Names the voce: probe and provenance. A generic «this voce» over a
            queue of several rows does not say which one is being closed. The
            publication clause is assembled by `rimappatureUtils` so that a missing
            revision or a missing instant each say which one is missing, instead of
            reading as one broken sentence. */}
        <Typography variant="body2">
          Sonda <span className={classes.sonda}>{voce.sn}</span>, che
          sorvegliava {voce.attrezzaturaEtichetta} — {voce.controlloEtichetta}.
          Voce aperta da: {descriviPubblicazioneCheHaChiuso(voce)}.
        </Typography>

        <WarningBanner>
          La sonda resta senza controllo: quell’attrezzatura non torna
          sorvegliata e nessun allarme lo dirà. Per rimetterla sotto
          sorveglianza usa «Riassegna».
        </WarningBanner>

        <div className={classes.campo} data-testid="campo-ragione">
          <Typography variant="subtitle2">Ragione</Typography>
          <TextField
            multiline
            rows={3}
            value={ragione}
            inputProps={{ 'aria-label': 'Ragione' }}
            placeholder="Perché questa sonda non viene riassegnata"
            onChange={(event) => setRagione(event.target.value)}
          />
          <FormHelperText>
            Resta a storico con la voce: la legge chi verrà dopo, o
            un’ispezione.
          </FormHelperText>
          {troppoLunga && (
            <Typography variant="body2" className={classes.errore}>
              {`La ragione non può superare i ${LUNGHEZZA_MASSIMA_RAGIONE} caratteri.`}
            </Typography>
          )}
        </div>

        {erroreServer && (
          <Typography variant="body2" className={classes.errore}>
            {erroreServer}
          </Typography>
        )}

        <div className={classes.pulsanti}>
          {/* A disabled button does not tell «no reason typed yet» from «the write
              is in flight», and the wait is several network round trips in a row
              (verification re-read, write, reload). `role="status"` is not
              decoration: a few seconds of silence is exactly when whoever cannot
              see the spinner clicks again. */}
          {salvataggioInCorso && (
            <div className={classes.statoSalvataggio} role="status">
              <SpinnerComponent size={18} className={classes.spinnerInline} />
              <Typography variant="body2">Archiviazione in corso…</Typography>
            </div>
          )}
          <ActionButton
            secondaryButton
            label="Annulla"
            disabled={salvataggioInCorso}
            onClick={onClose}
          />
          <ActionButton
            label="Archivia"
            disabled={!archiviabile}
            onClick={() => onArchivia(ripulita)}
          />
        </div>
      </div>
    </Modal>
  );
};

ArchiviaVoceModal.propTypes = {
  erroreServer: PropTypes.string,
  onArchivia: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  salvataggioInCorso: PropTypes.bool,
  voce: PropTypes.object.isRequired,
};

export default ArchiviaVoceModal;
